/*
  BEZA – Gift rules (metaobject „gift_rule")
  Duomenis kaskart parsiunčia per Section Rendering API (sections/gift-rules-data.liquid),
  todėl nepriklauso nuo to, ar DOM'e esantis JSON atnaujintas.
  Dovanos žymimos line item property `_gift_rule` = taisyklės handle.
*/
(function () {
  if (window.__bezaGiftRules) return;
  window.__bezaGiftRules = true;

  const PROP = '_gift_rule';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const LOCK_MSG = 'Negalite ištrinti ar didinti dovanos kiekio';
  let running = null;
  let queued = false;
  let timer = null;

  /* ---------- Duomenys ---------- */

  async function fetchState() {
    const res = await fetch(ROOT + '?sections=gift-rules-data&_=' + Date.now(), {
      headers: { Accept: 'application/json' },
    });
    const json = await res.json();
    const html = json['gift-rules-data'];
    if (!html) throw new Error('Negauta gift-rules-data sekcija');
    const el = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-gift-rules]');
    if (!el) throw new Error('Sekcijoje nerastas [data-gift-rules]');
    return JSON.parse(el.textContent);
  }

  // Visos stack=true taisyklės + viena stack=false taisyklė su didžiausiu slenksčiu
  function pickRules(rules) {
    const qualified = rules.filter((r) => r.qualified);
    const stackable = qualified.filter((r) => r.stack);
    const exclusive = qualified
      .filter((r) => !r.stack)
      .sort((a, b) => b.min - a.min)[0];
    return exclusive ? stackable.concat(exclusive) : stackable;
  }

  function desiredGifts(rules) {
    const map = new Map();
    pickRules(rules).forEach((rule) => {
      rule.gifts.forEach((g) => {
        if (!g.available) return;
        map.set(rule.handle + '|' + g.variantId, {
          handle: rule.handle,
          variantId: g.variantId,
          quantity: g.quantity,
        });
      });
    });
    return map;
  }

  async function post(path, body) {
    const res = await fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(path + ' ' + res.status);
    return res.json();
  }

  /* ---------- Perpiešimas ---------- */

  async function rerender() {
    const list = [
      { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      },
    ].filter((s) => s.section && document.getElementById(s.id));

    if (!list.length) return;

    const url =
      window.location.pathname + '?sections=' + list.map((s) => s.section).join(',');
    const sections = await fetch(url).then((r) => r.json());

    list.forEach((s) => {
      const html = sections[s.section];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector(s.selector);
      if (!source) return;
      const el = document.getElementById(s.id);
      const target = el.querySelector(s.selector) || el;
      target.innerHTML = source.innerHTML;
    });

    // assets/cart.js saugo currentItemCount ir, jei po kiekio keitimo prekių skaičius
    // „nepasikeitė", rodo klaidą „Į krepšelį galite pridėti tik X šios prekės".
    // Dovanų pridėjimas / šalinimas keičia skaičių temai nežinant, todėl jį atnaujiname.
    try {
      const cart = await fetch(ROOT + 'cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());
      document.querySelectorAll('cart-items').forEach((el) => {
        el.currentItemCount = cart.item_count;
      });
    } catch (e) {
      console.warn('[GiftRules] Nepavyko atnaujinti currentItemCount', e);
    }

    document.dispatchEvent(
      new CustomEvent('mini-cart:updated', { bubbles: true, detail: { source: 'gift-rules' } })
    );
  }

  /* ---------- Sinchronizacija ---------- */

  function sync() {
    if (running) {
      queued = true;
      return running;
    }
    running = doSync().finally(function () {
      running = null;
      if (queued) {
        queued = false;
        schedule();
      }
    });
    return running;
  }

  // Užbaigia visus laukiančius sinchronizavimus (naudojama prieš checkout)
  async function flush() {
    clearTimeout(timer);
    await sync();
    clearTimeout(timer);
    await sync();
  }

  async function doSync() {
    try {
      const data = await fetchState();
      if (!data || !Array.isArray(data.rules)) return;

      const desired = desiredGifts(data.rules);
      const present = new Set();
      const changes = [];

      // Dovanų produktai krepšelyje BE _gift_rule žymos (pvz. pridėti per
      // pasenusią krepšelio formą ar ranka) – visada šaliname
      (data.orphanLines || []).forEach((line) => {
        changes.push({ id: line.key, quantity: 0 });
      });

      (data.giftLines || []).forEach((line) => {
        const key = line.rule + '|' + line.variantId;
        const want = desired.get(key);

        if (!want || present.has(key)) {
          changes.push({ id: line.key, quantity: 0 });
          return;
        }
        present.add(key);
        if (line.quantity !== want.quantity) {
          changes.push({ id: line.key, quantity: want.quantity });
        }
      });

      const adds = [];
      desired.forEach((g, key) => {
        if (present.has(key)) return;
        adds.push({
          id: g.variantId,
          quantity: g.quantity,
          properties: { [PROP]: g.handle },
        });
      });

      if (!changes.length && !adds.length) return;

      for (const c of changes) {
        await post('cart/change.js', c);
      }
      if (adds.length) {
        await post('cart/add.js', { items: adds });
      }
      await rerender();
    } catch (e) {
      console.error('[GiftRules]', e);
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 250);
  }

  /* ---------- Dovanos eilutės apsauga ---------- */

  function showLockMessage(row) {
    const box =
      row.querySelector('.cart-item__details') ||
      row.querySelector('.product-content') ||
      row;
    let msg = box.querySelector('.gift-line-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'gift-line-msg';
      msg.setAttribute('role', 'alert');
      msg.style.cssText = 'color:#d20000;font-size:13px;line-height:1.4;margin:6px 0 0;';
      msg.textContent = LOCK_MSG;
      box.appendChild(msg);
    }
    msg.style.display = '';
    clearTimeout(msg._hideTimer);
    msg._hideTimer = setTimeout(function () {
      msg.style.display = 'none';
    }, 4000);
  }

  function guard(e) {
    const t = e.target;
    if (!t || !t.closest) return;
    const row = t.closest('[data-gift-line]');
    if (!row) return;
    const control = t.closest('cart-remove-button, .quantity__button, input[name^="updates["], .quantity__input');
    if (!control) return;
    if (e.type === 'keydown' && e.key !== 'Enter') return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (e.type === 'change' && 'defaultValue' in t) t.value = t.defaultValue;
    showLockMessage(row);
  }

  // capture fazė – suveikia anksčiau nei temos cart.js / quantity-input
  document.addEventListener('click', guard, true);
  document.addEventListener('change', guard, true);
  document.addEventListener('keydown', guard, true);

  /* ---------- Checkout apsauga ----------
     Krepšelio forma siunčia updates[eilutės_raktas]=kiekis. Jei forma pasenusi
     (pvz. dovana jau pašalinta / pakeista), Shopify pagal raktą iš naujo
     prideda tą variantą BE _gift_rule žymos – taip atsirasdavo dubliuotos
     dovanos. Visi kiekiai krepšelyje keičiami per AJAX, todėl checkout metu
     kiekių siųsti nereikia – juos išjungiame. */
  function disableQuantityInputs(form) {
    const inputs = [];
    form.querySelectorAll('[name^="updates["]').forEach((el) => inputs.push(el));
    if (form.id) {
      document
        .querySelectorAll('[form="' + form.id + '"][name^="updates["]')
        .forEach((el) => inputs.push(el));
    }
    inputs.forEach((el) => {
      el.disabled = true;
    });
    // Jei puslapis neišeina (pvz. checkout atšauktas) – grąžiname po kelių sekundžių
    setTimeout(() => {
      inputs.forEach((el) => {
        el.disabled = false;
      });
    }, 4000);
  }

  document.addEventListener(
    'submit',
    function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart(\?|$)/.test(form.getAttribute('action') || '')) return;
      const submitter = e.submitter;
      const isCheckout =
        (submitter && submitter.name === 'checkout') ||
        form.querySelector('input[name="checkout"]');
      if (!isCheckout) return;
      disableQuantityInputs(form);
    },
    true
  );

  // Paspaudus „Saugus atsiskaitymas" – iškart užbaigiame dovanų sinchronizavimą
  document.addEventListener(
    'pointerdown',
    function (e) {
      if (e.target.closest && e.target.closest('button[name="checkout"]')) {
        flush();
      }
    },
    true
  );

  /* ---------- Įvykiai ---------- */

  document.addEventListener('cart:updated', schedule);
  document.addEventListener('mini-cart:updated', function (e) {
    if (e.detail && e.detail.source === 'gift-rules') return;
    schedule();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  window.BezaGiftRules = { sync: sync, flush: flush };
})();